import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sunrise, Moon, Brain, Target, Sparkles, ChevronRight,
  Loader2, ArrowLeft, Eye
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { DictationButton } from '../components/DictationButton';

const FEELINGS = [
  { id: 'calm', en: 'Calm', tr: 'Sakin', color: 'bg-blue-500/20 border-blue-500/40 text-blue-300' },
  { id: 'focused', en: 'Focused', tr: 'Odaklanmış', color: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' },
  { id: 'scattered', en: 'Scattered', tr: 'Dağınık', color: 'bg-amber-500/20 border-amber-500/40 text-amber-300' },
  { id: 'heavy', en: 'Heavy', tr: 'Ağır', color: 'bg-purple-500/20 border-purple-500/40 text-purple-300' },
  { id: 'energized', en: 'Energized', tr: 'Enerjik', color: 'bg-rose-500/20 border-rose-500/40 text-rose-300' },
];

export const MorningGuidance = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [step, setStep] = useState(0); // 0=check existing, 1-4=input steps, 5=generating, 6=result
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [existingOutput, setExistingOutput] = useState(null);

  // Input state
  const [sleepQuality, setSleepQuality] = useState(5);
  const [feeling, setFeeling] = useState('');
  const [mentalField, setMentalField] = useState('');
  const [oneWin, setOneWin] = useState('');
  const [showDream, setShowDream] = useState(false);
  const [dream, setDream] = useState('');

  // Output state
  const [output, setOutput] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkExisting = async () => {
    try {
      const response = await api.get('/checkin/morning/today');
      if (response.data.submitted) {
        setExistingOutput(response.data);
        setOutput(response.data.output);
        setStep(6);
      } else {
        setStep(1);
      }
    } catch {
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const submitCalibration = async () => {
    setGenerating(true);
    setStep(5);
    try {
      const response = await api.post('/checkin/morning', {
        sleep_quality: sleepQuality,
        feeling,
        mental_field: mentalField,
        one_win: oneWin,
        dream: showDream && dream ? dream : null,
      });
      setOutput(response.data.output);
      setStep(6);
    } catch (error) {
      console.error('Morning calibration error:', error);
      toast.error(language === 'tr' ? 'Kalibrasyon oluşturulamadı' : 'Failed to generate calibration');
      setStep(4);
    } finally {
      setGenerating(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return feeling !== '';
    if (step === 2) return mentalField.trim().length > 0;
    if (step === 3) return oneWin.trim().length > 0;
    if (step === 4) return true;
    return false;
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      submitCalibration();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Sunrise className="w-12 h-12 text-[#D4AF37] animate-pulse" />
      </div>
    );
  }

  // RESULT SCREEN
  if (step === 6 && output) {
    return <DailyOutput output={output} language={language} navigate={navigate} existingData={existingOutput} />;
  }

  // GENERATING SCREEN
  if (step === 5) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-6" />
          <h2 className="font-serif text-2xl text-[#F8F5F2] mb-2">
            {language === 'tr' ? 'Navigasyonunuz hazırlanıyor...' : 'Calibrating your day...'}
          </h2>
          <p className="text-[#6B7280] font-sans text-sm">
            {language === 'tr' ? 'Kalıpları analiz ediyor, yönünüzü buluyor' : 'Analyzing patterns, finding your direction'}
          </p>
        </motion.div>
      </div>
    );
  }

  // INPUT FLOW
  return (
    <div className="min-h-screen relative py-12 px-6">
      <div className="max-w-lg mx-auto">
        {/* Back button */}
        <button
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/dashboard')}
          className="text-white/50 hover:text-white transition-colors mb-6 flex items-center gap-2 text-sm font-sans uppercase tracking-widest"
          data-testid="morning-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {step > 1 ? (language === 'tr' ? 'Geri' : 'Back') : (language === 'tr' ? 'Panel' : 'Dashboard')}
        </button>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                s === step ? 'w-8 bg-[#D4AF37]' : s < step ? 'w-4 bg-[#D4AF37]/50' : 'w-4 bg-white/10'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 1: State of Being */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="space-y-8"
            >
              <div className="text-center">
                <Sunrise className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl text-[#F8F5F2] mb-1">
                  {language === 'tr' ? 'Varlık Durumu' : 'State of Being'}
                </h2>
                <p className="text-[#6B7280] font-sans text-sm">
                  {language === 'tr' ? 'Bedeninize ve zihninize giriş yapın' : 'Ground into your body and mind'}
                </p>
              </div>

              {/* Sleep slider */}
              <div data-testid="sleep-quality-section">
                <label className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans block mb-3">
                  {language === 'tr' ? 'Nasıl uyudunuz?' : 'How did you sleep?'}
                </label>
                <div className="flex items-center gap-4">
                  <span className="text-[#6B7280] text-sm font-mono w-6 text-center">{sleepQuality}</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={sleepQuality}
                    onChange={(e) => setSleepQuality(Number(e.target.value))}
                    className="flex-1 accent-[#D4AF37] h-1.5"
                    data-testid="sleep-slider"
                  />
                  <span className="text-[#6B7280] text-xs">/10</span>
                </div>
              </div>

              {/* Feeling selector */}
              <div data-testid="feeling-section">
                <label className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans block mb-3">
                  {language === 'tr' ? 'Şu an nasıl hissediyorsunuz?' : 'How do you feel right now?'}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {FEELINGS.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFeeling(f.id)}
                      className={`px-4 py-2.5 rounded-xl border text-sm font-sans transition-all duration-200 ${
                        feeling === f.id
                          ? f.color + ' ring-1 ring-white/20 scale-105'
                          : 'bg-white/5 border-white/10 text-[#A1A1AA] hover:bg-white/10'
                      }`}
                      data-testid={`feeling-${f.id}`}
                    >
                      {language === 'tr' ? f.tr : f.en}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Mental Field */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="space-y-6"
            >
              <div className="text-center">
                <Brain className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl text-[#F8F5F2] mb-1">
                  {language === 'tr' ? 'Zihin Alanı' : 'Mental Field'}
                </h2>
                <p className="text-[#6B7280] font-sans text-sm">
                  {language === 'tr' ? 'Bu sabah aklınızda ne var?' : "What's on your mind this morning?"}
                </p>
              </div>

              <div className="relative" data-testid="mental-field-section">
                <textarea
                  value={mentalField}
                  onChange={(e) => setMentalField(e.target.value)}
                  placeholder={language === 'tr'
                    ? 'Düşüncelerinizi, endişelerinizi, heyecanınızı yazın...'
                    : 'Write your thoughts, worries, excitement...'}
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-4 pr-12 text-[#F8F5F2] placeholder-[#4B5563] font-sans text-sm resize-none focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                  data-testid="mental-field-input"
                />
                <div className="absolute top-3 right-3">
                  <DictationButton onTranscript={(text) => setMentalField(prev => prev ? prev + ' ' + text : text)} />
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Direction Lock */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="space-y-6"
            >
              <div className="text-center">
                <Target className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl text-[#F8F5F2] mb-1">
                  {language === 'tr' ? 'Yön Kilidi' : 'Direction Lock'}
                </h2>
                <p className="text-[#6B7280] font-sans text-sm">
                  {language === 'tr'
                    ? 'Bugün tek bir anlamlı kazanım olsaydı, ne olurdu?'
                    : 'If today had one meaningful win, what would it be?'}
                </p>
              </div>

              <div className="relative" data-testid="one-win-section">
                <input
                  type="text"
                  value={oneWin}
                  onChange={(e) => setOneWin(e.target.value)}
                  placeholder={language === 'tr' ? 'Bugünkü kazanımınız...' : 'Your one win for today...'}
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-4 pr-12 text-[#F8F5F2] placeholder-[#4B5563] font-sans text-sm focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                  data-testid="one-win-input"
                  onKeyDown={(e) => e.key === 'Enter' && canProceed() && handleNext()}
                />
                <div className="absolute top-3 right-3">
                  <DictationButton onTranscript={(text) => setOneWin(prev => prev ? prev + ' ' + text : text)} />
                </div>
              </div>

              <p className="text-center text-[#4B5563] text-xs font-sans italic">
                {language === 'tr'
                  ? 'Bu tek soru bugün bunalmayı çözer ve harekete geçirir'
                  : 'This single question collapses overwhelm and creates agency'}
              </p>
            </motion.div>
          )}

          {/* STEP 4: Dream Layer (Optional) */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="space-y-6"
            >
              <div className="text-center">
                <Moon className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl text-[#F8F5F2] mb-1">
                  {language === 'tr' ? 'Rüya Katmanı' : 'Dream Layer'}
                </h2>
                <p className="text-[#6B7280] font-sans text-sm">
                  {language === 'tr' ? 'İsteğe bağlı — geçen gece önemli bir rüya gördünüz mü?' : 'Optional — did you have any significant dreams?'}
                </p>
              </div>

              <div className="flex gap-3 justify-center" data-testid="dream-toggle-section">
                <button
                  onClick={() => { setShowDream(false); setDream(''); }}
                  className={`px-6 py-2.5 rounded-xl border text-sm font-sans transition-all ${
                    !showDream
                      ? 'bg-white/10 border-white/20 text-[#F8F5F2]'
                      : 'bg-white/5 border-white/10 text-[#6B7280] hover:bg-white/10'
                  }`}
                  data-testid="dream-no-btn"
                >
                  {language === 'tr' ? 'Hayır' : 'No'}
                </button>
                <button
                  onClick={() => setShowDream(true)}
                  className={`px-6 py-2.5 rounded-xl border text-sm font-sans transition-all ${
                    showDream
                      ? 'bg-purple-500/20 border-purple-500/40 text-purple-300'
                      : 'bg-white/5 border-white/10 text-[#6B7280] hover:bg-white/10'
                  }`}
                  data-testid="dream-yes-btn"
                >
                  {language === 'tr' ? 'Evet' : 'Yes'}
                </button>
              </div>

              <AnimatePresence>
                {showDream && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="relative overflow-hidden"
                  >
                    <textarea
                      value={dream}
                      onChange={(e) => setDream(e.target.value)}
                      placeholder={language === 'tr'
                        ? 'Rüyanızı anlatın...'
                        : 'Describe your dream...'}
                      rows={3}
                      className="w-full bg-white/5 border border-purple-500/20 rounded-xl p-4 pr-12 text-[#F8F5F2] placeholder-[#4B5563] font-sans text-sm resize-none focus:outline-none focus:border-purple-500/40 transition-colors"
                      data-testid="dream-input"
                    />
                    <div className="absolute top-3 right-3">
                      <DictationButton onTranscript={(text) => setDream(prev => prev ? prev + ' ' + text : text)} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation button */}
        {step >= 1 && step <= 4 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10"
          >
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="w-full py-4 rounded-2xl font-sans text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed bg-[#D4AF37] text-[#030305] hover:bg-[#FFBF00]"
              data-testid="morning-next-btn"
            >
              {step === 4
                ? (language === 'tr' ? 'Günümü Kalibre Et' : 'Calibrate My Day')
                : (language === 'tr' ? 'Devam' : 'Continue')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ==================== DAILY OUTPUT SCREEN ====================

const DailyOutput = ({ output, language, navigate, existingData }) => {
  const toneIcons = {
    the_warrior: '⚔️', the_magician: '✨', the_hermit: '🏔️', the_builder: '🧱',
    the_explorer: '🧭', the_lover: '💫', the_emperor: '👑', the_fool: '🃏',
    the_star: '⭐', the_integrator: '☯️', the_climber: '🧗', the_caregiver: '🤲',
    the_high_priestess: '🌙', the_chariot: '🔥', the_sage: '📿', strength: '🦁',
  };

  return (
    <div className="min-h-screen relative py-12 px-6">
      <div className="max-w-lg mx-auto">
        {/* Back */}
        <button
          onClick={() => navigate('/dashboard')}
          className="text-white/50 hover:text-white transition-colors mb-8 flex items-center gap-2 text-sm font-sans uppercase tracking-widest"
          data-testid="output-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'tr' ? 'Panel' : 'Dashboard'}
        </button>

        {/* Daily Tone Badge */}
        {output.daily_tone && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
              <span className="text-lg">{toneIcons[output.daily_tone.id] || '☀️'}</span>
              <span className="text-[#D4AF37] font-sans text-sm font-medium tracking-wide">
                {output.daily_tone.name}
              </span>
            </div>
            {output.daily_tone.message && (
              <p className="text-[#A1A1AA] font-serif text-sm italic mt-3 max-w-sm mx-auto">
                "{output.daily_tone.message}"
              </p>
            )}
          </motion.div>
        )}

        {/* Reflection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-panel rounded-2xl p-5 mb-4"
          data-testid="output-reflection"
        >
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-sans">
              {language === 'tr' ? 'Yansıma' : 'Reflection'}
            </span>
          </div>
          <p className="text-[#D1D1D6] font-sans text-sm leading-relaxed">{output.reflection}</p>
        </motion.div>

        {/* Focus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl p-5 mb-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20"
          data-testid="output-focus"
        >
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-sans">
              {language === 'tr' ? 'Odak' : 'Focus'}
            </span>
          </div>
          <p className="text-[#F8F5F2] font-serif text-lg leading-relaxed">{output.focus}</p>
        </motion.div>

        {/* Micro Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-panel rounded-2xl p-5 mb-4"
          data-testid="output-micro-action"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-xs uppercase tracking-wider text-emerald-400 font-sans">
              {language === 'tr' ? 'İlk Adım' : 'First Move'}
            </span>
          </div>
          <p className="text-[#D1D1D6] font-sans text-sm">{output.micro_action}</p>
        </motion.div>

        {/* Pattern Awareness */}
        {output.pattern_awareness && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl p-5 mb-4 bg-amber-500/5 border border-amber-500/20"
            data-testid="output-pattern"
          >
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-wider text-amber-400 font-sans">
                {language === 'tr' ? 'Kalıp Farkındalığı' : 'Pattern Awareness'}
              </span>
            </div>
            <p className="text-[#D1D1D6] font-sans text-sm italic">{output.pattern_awareness}</p>
          </motion.div>
        )}

        {/* Dream Insight */}
        {output.dream_insight && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="rounded-2xl p-5 mb-4 bg-purple-500/5 border border-purple-500/20"
            data-testid="output-dream"
          >
            <div className="flex items-center gap-2 mb-3">
              <Moon className="w-4 h-4 text-purple-400" />
              <span className="text-xs uppercase tracking-wider text-purple-400 font-sans">
                {language === 'tr' ? 'Rüya İçgörüsü' : 'Dream Insight'}
              </span>
            </div>
            <p className="text-[#D1D1D6] font-sans text-sm italic">{output.dream_insight}</p>
          </motion.div>
        )}

        {/* Go to Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-4 rounded-2xl font-sans text-sm uppercase tracking-widest transition-all duration-300 bg-[#D4AF37] text-[#030305] hover:bg-[#FFBF00] flex items-center justify-center gap-2"
            data-testid="go-to-dashboard-button"
          >
            {language === 'tr' ? 'Güne Başla' : 'Begin Your Day'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default MorningGuidance;
